// Match the header's progressive enhancement, including theme-editor remounts.
if (typeof customElements !== 'undefined' && !customElements.get('footer-localization')) {
  customElements.define('footer-localization', class extends HTMLElement {
    connectedCallback() {
      this._localizationAbort?.abort();
      this._localizationAbort = new AbortController();
      for (const form of this.querySelectorAll('form')) {
        if (typeof form.requestSubmit !== 'function') continue;
        const select = form.querySelector('select');
        if (!select) continue;
        select.addEventListener('change', () => form.requestSubmit(), { signal: this._localizationAbort.signal });
        form.setAttribute('data-localization-enhanced', '');
      }
    }
    disconnectedCallback() { this._localizationAbort?.abort(); }
  });
}

export function createJourneyNavigationController({
  drawer,
  groups = [],
  hamburger,
  closeControl,
  inertTargets = [],
  bodyStyle,
  isDesktop = () => false,
  setTimer = (callback, delay) => setTimeout(callback, delay),
  clearTimer = (timer) => clearTimeout(timer)
}) {
  let scrollLocked = false;
  let previousBodyOverflow = '';
  let suppressNextDrawerToggle = false;
  let closeTimer;

  const summaryFor = (group) => group.summary ?? group.querySelector?.('[data-nav-summary]');
  const setExpanded = (control, expanded) => control?.setAttribute?.('aria-expanded', String(expanded));

  const syncGroupState = (group) => setExpanded(summaryFor(group), Boolean(group.open));

  const cancelGroupClose = () => {
    if (closeTimer === undefined) return;
    clearTimer(closeTimer);
    closeTimer = undefined;
  };

  const closeGroups = (except) => {
    cancelGroupClose();
    for (const group of groups) {
      if (group !== except) group.open = false;
      syncGroupState(group);
    }
  };

  const openGroup = (activeGroup) => {
    cancelGroupClose();
    closeGroups(activeGroup);
    activeGroup.open = true;
    syncGroupState(activeGroup);
  };

  const scheduleGroupClose = (activeGroup, delay = 180) => {
    cancelGroupClose();
    closeTimer = setTimer(() => {
      closeTimer = undefined;
      if (activeGroup.open) activeGroup.open = false;
      syncGroupState(activeGroup);
    }, delay);
  };

  const setPageInert = (inert) => {
    for (const target of inertTargets) target.inert = inert;
  };

  const lockScroll = () => {
    if (!bodyStyle || scrollLocked) return;
    previousBodyOverflow = bodyStyle.overflow ?? '';
    bodyStyle.overflow = 'hidden';
    scrollLocked = true;
  };

  const unlockScroll = () => {
    if (!bodyStyle || !scrollLocked) return;
    bodyStyle.overflow = previousBodyOverflow;
    scrollLocked = false;
  };

  const releaseMobileState = () => {
    setPageInert(false);
    unlockScroll();
  };

  const setDrawerOpen = (open) => {
    if (Boolean(drawer.open) === open) return false;
    suppressNextDrawerToggle = true;
    drawer.open = open;
    return true;
  };

  const closeDrawer = ({ restoreFocus = false } = {}) => {
    setDrawerOpen(false);
    closeGroups();
    releaseMobileState();
    setExpanded(hamburger, false);
    if (restoreFocus) hamburger?.focus?.();
  };

  const handleDrawerToggle = () => {
    const suppressFocus = suppressNextDrawerToggle;
    suppressNextDrawerToggle = false;
    if (isDesktop()) {
      setDrawerOpen(true);
      releaseMobileState();
      setExpanded(hamburger, true);
      return;
    }

    setExpanded(hamburger, Boolean(drawer.open));
    if (drawer.open) {
      setPageInert(true);
      lockScroll();
      if (!suppressFocus) closeControl?.focus?.();
    } else {
      releaseMobileState();
      if (!suppressFocus) hamburger?.focus?.();
    }
  };

  const handleGroupToggle = (activeGroup) => {
    if (activeGroup.open) closeGroups(activeGroup);
    syncGroupState(activeGroup);
  };

  const handleOutsideInteraction = () => {
    closeGroups();
  };

  const handleKeydown = (event) => {
    if (event.key !== 'Escape') return false;
    const hadOpenGroup = groups.some((group) => group.open);
    const hadOpenDrawer = !isDesktop() && drawer.open;
    if (hadOpenGroup) closeGroups();
    if (hadOpenDrawer) closeDrawer({ restoreFocus: true });
    if (hadOpenGroup || hadOpenDrawer) event.preventDefault?.();
    return hadOpenGroup || hadOpenDrawer;
  };

  const handleBreakpoint = () => {
    closeGroups();
    releaseMobileState();
    if (isDesktop()) {
      setDrawerOpen(true);
      setExpanded(hamburger, true);
    } else {
      setDrawerOpen(false);
      setExpanded(hamburger, false);
    }
  };

  const destroy = () => {
    cancelGroupClose();
    closeGroups();
    releaseMobileState();
  };

  return {
    cancelGroupClose,
    closeDrawer,
    closeGroups,
    destroy,
    handleBreakpoint,
    handleDrawerToggle,
    handleGroupToggle,
    handleKeydown,
    handleOutsideInteraction,
    openGroup,
    scheduleGroupClose
  };
}

export function createHeaderScrollController({
  root,
  getScrollY = () => 0,
  shouldRemainVisible = () => false,
  deltaThreshold = 8,
  topBoundary = 16
}) {
  let lastScrollY = Math.max(0, getScrollY());

  const reveal = () => root.removeAttribute('data-header-hidden');

  const handleScroll = () => {
    const currentScrollY = Math.max(0, getScrollY());
    if (currentScrollY <= topBoundary || shouldRemainVisible()) {
      reveal();
      lastScrollY = currentScrollY;
      return;
    }

    const delta = currentScrollY - lastScrollY;
    if (Math.abs(delta) < deltaThreshold) return;
    root.toggleAttribute('data-header-hidden', delta > 0);
    lastScrollY = currentScrollY;
  };

  return {
    destroy: reveal,
    handleScroll,
    reveal
  };
}

const canBootstrap =
  typeof document !== 'undefined' &&
  typeof HTMLElement !== 'undefined' &&
  typeof customElements !== 'undefined';

if (canBootstrap) {
  class JourneyNavigation extends HTMLElement {
    connectedCallback() {
      if (this._abortController) return;

      const drawer = this.querySelector('[data-mobile-drawer]');
      const hamburger = drawer?.querySelector(':scope > summary');
      const closeControl = this.querySelector('[data-nav-close]');
      const navigationTree = this.querySelector('[data-nav-tree]');
      const groups = [...this.querySelectorAll('[data-nav-group]')];
      if (!drawer || !hamburger || !closeControl || !navigationTree) return;

      const desktopQuery = window.matchMedia('(min-width: 1360px)');
      const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      const stickyRoot = this.closest?.('.shopify-section-group-header-group') ?? this;
      const localizationForms = [...this.querySelectorAll('.site-header__localization form')];
      const inertTargets = [
        ...this.querySelectorAll('[data-drawer-inert-target]'),
        document.querySelector('main'),
        document.querySelector('[data-site-footer]')
      ].filter(Boolean);

      this._abortController = new AbortController();
      const { signal } = this._abortController;
      this._controller = createJourneyNavigationController({
        drawer,
        groups,
        hamburger,
        closeControl,
        inertTargets,
        bodyStyle: document.body.style,
        isDesktop: () => desktopQuery.matches
      });
      this._scrollController = createHeaderScrollController({
        root: stickyRoot,
        getScrollY: () => window.scrollY ?? 0,
        shouldRemainVisible: () =>
          groups.some((group) => group.open) ||
          (!desktopQuery.matches && drawer.open) ||
          this.contains(document.activeElement)
      });

      this.setAttribute('data-enhanced', '');
      if (localizationForms.length > 0 && localizationForms.every((form) => typeof form.requestSubmit === 'function')) {
        this.setAttribute('data-localization-enhanced', '');
      }
      this._controller.handleBreakpoint();

      const searchDialog = this.querySelector('[data-header-search]');
      const searchTrigger = this.querySelector('.site-header__search');
      if (searchDialog && searchTrigger && typeof searchDialog.showModal === 'function') {
        let previousOverflow;
        searchTrigger.setAttribute('aria-haspopup', 'dialog');
        searchTrigger.setAttribute('aria-expanded', 'false');
        searchTrigger.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this._controller.handleOutsideInteraction();
          this._scrollController.reveal();
          previousOverflow = document.body.style.overflow;
          searchDialog.showModal();
          document.body.style.overflow = 'hidden';
          searchTrigger.setAttribute('aria-expanded', 'true');
          searchDialog.querySelector('input').focus();
        }, { signal });
        const restoreSearchState = () => {
          if (previousOverflow !== undefined) document.body.style.overflow = previousOverflow;
          previousOverflow = undefined;
          searchTrigger.setAttribute('aria-expanded', 'false');
        };
        searchDialog.querySelector('[data-search-close]').addEventListener('click', () => searchDialog.close(), { signal });
        searchDialog.addEventListener('keydown', (event) => {
          if (event.key !== 'Tab') return;
          const first = searchDialog.querySelector('input');
          const last = searchDialog.querySelector('[data-search-close]');
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }, { signal });
        searchDialog.addEventListener('click', (event) => {
          const bounds = searchDialog.getBoundingClientRect();
          if (event.target === searchDialog && (event.clientY > bounds.bottom || event.clientY < bounds.top || event.clientX < bounds.left || event.clientX > bounds.right)) searchDialog.close();
        }, { signal });
        searchDialog.addEventListener('close', () => {
          restoreSearchState();
          searchTrigger.focus({ preventScroll: true });
        }, { signal });
        signal.addEventListener('abort', () => {
          if (searchDialog.open) searchDialog.close();
          restoreSearchState();
        }, { once: true });
      }

      drawer.addEventListener('toggle', (event) => {
        if (event.target !== drawer) return;
        this._controller.handleDrawerToggle();
        this._scrollController.reveal();
      }, { signal });
      closeControl.addEventListener('click', () => {
        this._controller.closeDrawer({ restoreFocus: true });
        this._scrollController.reveal();
      }, { signal });
      for (const group of groups) {
        group.addEventListener('toggle', () => {
          this._controller.handleGroupToggle(group);
          if (group.open) this._scrollController.reveal();
        }, { signal });
        group.addEventListener('pointerenter', () => {
          if (!desktopQuery.matches || !hoverQuery.matches) return;
          this._controller.openGroup(group);
          this._scrollController.reveal();
        }, { signal });
        group.addEventListener('pointerleave', () => {
          if (!desktopQuery.matches || !hoverQuery.matches) return;
          this._controller.scheduleGroupClose(group);
        }, { signal });
        const summary = group.querySelector('[data-nav-summary]');
        summary?.addEventListener('focus', () => {
          if (desktopQuery.matches) {
            this._controller.openGroup(group);
            this._scrollController.reveal();
          }
        }, { signal });
        summary?.addEventListener('click', (event) => {
          event.preventDefault();
          if (!desktopQuery.matches && group.open) {
            group.open = false;
            this._controller.handleGroupToggle(group);
          } else {
            this._controller.openGroup(group);
          }
        }, { signal });
      }
      for (const form of localizationForms) {
        const select = form.querySelector('select');
        select?.addEventListener('change', () => {
          if (this.hasAttribute('data-localization-enhanced')) form.requestSubmit();
        }, { signal });
      }
      desktopQuery.addEventListener('change', () => {
        this._controller.handleBreakpoint();
        this._scrollController.reveal();
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!navigationTree.contains(event.target)) this._controller.handleOutsideInteraction();
      }, { signal });
      navigationTree.addEventListener('focusout', (event) => {
        // Safari can blur a summary with no relatedTarget before an anchor click.
        // Outside pointerdown already dismisses pointer interactions; only dismiss
        // focus changes with a known destination outside the navigation.
        // Mobile dismissal is handled by outside pointerdown, Close and Escape.
        if (desktopQuery.matches && event.relatedTarget && !navigationTree.contains(event.relatedTarget)) this._controller.handleOutsideInteraction();
      }, { signal });
      document.addEventListener('keydown', (event) => this._controller.handleKeydown(event), { signal });
      window.addEventListener?.('scroll', () => this._scrollController.handleScroll(), { passive: true, signal });
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._controller?.destroy();
      this._scrollController?.destroy();
      this._abortController = undefined;
      this._controller = undefined;
      this._scrollController = undefined;
    }
  }

  if (!customElements.get('journey-navigation')) {
    customElements.define('journey-navigation', JourneyNavigation);
  }
}

